import React, { FC, useEffect, useState } from 'react'
import List from '../../components/List'
import { IDeckGame, IDeckStatusTotal } from './types'
import { numberToCurrency } from '../../utils/objectUtils'
import {
  compareDates,
  compareNegativeCurrency,
  compareNumbers,
  stringCompare,
  transformToNumberAndCompare
} from '../../utils/sorters'
import { Tag } from 'antd'
import { renderCurrency, renderPercetage } from '../../utils/stringUtils'
import Button from '../../components/UI/Button'
import { useAuth } from '../../context/AuthProvider/useAuth'

type IDeckGames = IDeckGame[]

interface LocationTableProps {
  id: number;
  name: string;
  listGames: IDeckGames;
  checkBodyChange: () => void;
  total?: IDeckStatusTotal;
  sentValues: {locationId: string, gameId: string}[];
  resetSentValues: () => void;
}

function LocationTable(props: LocationTableProps) {
  const {canEdit} = useAuth()
  const totalPrize =  {
    ...props.total,
    form: "TOTAL"
  }
  const locationName = {
    form: props.name,
    gameId: "",
    title: "",
    openedTime: "",
    serial: "",
    tickets: "",
    payout: "",
    totalTicketCost: "",
    totalTicketPrizes: "",
    currentTotalWager: "",
    currentTotalWon: "",
    actualPayout: null,
    prizesWon: "",
    remainingTicketPct: "",
    inventoryLeft: "",
  }

  const listGames = [locationName, ...props.listGames, totalPrize]

  const sentValues: {locationId: string, gameId: string}[] = props.sentValues

  useEffect(()=> {
    props.resetSentValues()
  },[props.listGames])

  const manageInputs = (checked: boolean, locationId: number) => {
    const checkboxes: NodeListOf<HTMLInputElement & { type: "checkbox" }> = document.querySelectorAll("input[type=checkbox]")
    if(checkboxes) checkboxes.forEach((checkbox) =>  {
      if(checkbox.id === 'form' || checkbox.disabled || checkbox.dataset.location !== `${locationId}`) return
      checkbox.checked = checked
    })
    props.checkBodyChange()
  }

  const isFistOrLastRow = (record: IDeckGame) => record.form === 'TOTAL' ||  record.form === props.name 

  return (
    <div >
      <List
        pagination={false}
        style={{marginBottom: 30}}
        rowClassName={(record, index) => {
          if(isFistOrLastRow(record as IDeckGame)) return 'list-component__last-row'
          return ''
        }}
        columns={[
          {
            title: 'Form',
            dataIndex: 'form',
            key: 'form',
            render: (value: string) => <div style={{width: 150}}>{value}</div>,
            sorter: (a: IDeckGame, b: IDeckGame) => transformToNumberAndCompare(a.form, b.form, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 200
          },
          {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (value: string) => value,
            sorter: (a: IDeckGame, b: IDeckGame) => stringCompare(a.title, b.title, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 200
          },
          {
            title: 'Opened Time',
            dataIndex: 'openedTime',
            key: 'openedTime',
            render: (value: string) => value,
            sorter: (a: IDeckGame, b: IDeckGame) => compareDates(a.openedTime, b.openedTime, 'MM-DD-YYYY HH:mm:ss', isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 200
          },
          {
            title: 'Serial',
            dataIndex: 'serial',
            key: 'serial',
            render: (value: string) => value,
            sorter: (a: IDeckGame, b: IDeckGame) => transformToNumberAndCompare(a.serial, b.serial, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 130
          },
          {
            title: 'Tickets',
            dataIndex: 'tickets',
            key: 'tickets',
            render: (value: string) => value,
            sorter: (a: IDeckGame, b: IDeckGame) => transformToNumberAndCompare(a.tickets, b.tickets, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 130
          },
          {
            title: 'Payout',
            dataIndex: 'payout',
            key: 'payout',
            render: (value: string, Record: IDeckGame, index: number) => (index === 0 && '') || (value && value + '%'),
            sorter: (a: IDeckGame, b: IDeckGame) => compareNumbers(parseFloat(a.payout), parseFloat(b.payout), isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 100
          },
          {
            title: 'Total Ticket Cost',
            dataIndex: 'totalTicketCost',
            key: 'totalTicketCost',
            render: (value: string) => renderCurrency(value),
            sorter: (a: IDeckGame, b: IDeckGame) => transformToNumberAndCompare(a.totalTicketCost, b.totalTicketCost, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 150
          },
          {
            title: 'Total Ticket Prizes',
            dataIndex: 'totalTicketPrizes',
            key: 'totalTicketPrizes',
            render: (value: string) => renderCurrency(value),
            sorter: (a: IDeckGame, b: IDeckGame) => transformToNumberAndCompare(a.totalTicketPrizes, b.totalTicketPrizes, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 150
          },
          {
            title: 'Current Total Wager',
            dataIndex: 'currentTotalWager',
            key: 'currentTotalWager',
            render: (value: string, Record: IDeckGame, index: number) => (!index ? '' : value ? numberToCurrency(value) : '$0.00'),
            sorter: (a: IDeckGame, b: IDeckGame) => compareNegativeCurrency(a.currentTotalWager, b.currentTotalWager, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 150
          },
          {
            title: 'Current Total Won',
            dataIndex: 'currentTotalWon',
            key: 'currentTotalWon',
            render: (value: string, Record: IDeckGame, index: number) => (!index ? '' : value ? numberToCurrency(value) : '$0.00'),
            sorter: (a: IDeckGame, b: IDeckGame) => compareNegativeCurrency(a.currentTotalWon, b.currentTotalWon, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 150
          },
          {
            title: 'Actual Payout',
            dataIndex: 'actualPayout',
            key: 'actualPayout',
            render: (value: number, record: IDeckGame, index: number) => value !== null ? value + '%' : '',
            sorter: (a: IDeckGame, b: IDeckGame) => {
              return compareNumbers(parseFloat(a.actualPayout), parseFloat(b.actualPayout), isFistOrLastRow(a) || isFistOrLastRow(b))
            },
            width: 150
          },
          {
            title: 'Prizes Won',
            dataIndex: 'prizesWon',
            key: 'prizesWon',
            render: (value: string) => value,
            sorter: (a: IDeckGame, b: IDeckGame) => transformToNumberAndCompare(a.prizesWon, b.prizesWon, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 100
          },
          {
            title: 'Remaining Ticket Pct',
            dataIndex: 'remainingTicketPct',
            key: 'remainingTicketPct',
            render: (value: string) => renderPercetage(value),
            sorter: (a: IDeckGame, b: IDeckGame) => compareNumbers(parseFloat(a.remainingTicketPct), parseFloat(b.remainingTicketPct), isFistOrLastRow(a) || isFistOrLastRow(b) ),
            width: 150
          },
          {
            title: 'Inventory Left',
            dataIndex: 'inventoryLeft',
            key: 'inventoryLeft',
            render: (value: string, Record: IDeckGame, index: number) => (!index ? '' : value ? numberToCurrency(value) : '$0.00'),
            sorter: (a: IDeckGame, b: IDeckGame) => compareNegativeCurrency(a.inventoryLeft, b.inventoryLeft, isFistOrLastRow(a) || isFistOrLastRow(b)),
            width: 150
          },
          {
            title: "Actions",
            dataIndex: '',
            key: 'checkBox',
            width: 150,
            render: (value: IDeckGame, Record: IDeckGame, index: number) => (
              <LastColumnRow 
                checkbody={props.checkBodyChange} 
                disabled={!canEdit} index={index} 
                listGamesLength={listGames.length} 
                sentValues={sentValues} value={value} 
                manageInputs={manageInputs} 
                locationId={props.id}
              />
            ),
          }
        ]}
        rows={listGames}
      />
    </div>
  )
}

interface LastColumnRowProps{
  index: number;
  listGamesLength: number;
  sentValues: {locationId: string, gameId: string}[];
  value: IDeckGame;
  manageInputs: (check: boolean, locationId:number) => void;
  locationId: number;
  disabled: boolean;
  checkbody: () => void
}

const LastColumnRow: FC<LastColumnRowProps> = ({index, listGamesLength, sentValues, value, manageInputs, locationId, disabled, checkbody}) => {
  if(index === listGamesLength - 1) return (<></>)
  if(index === 0 ) return (
    <>
      <Button disabled={disabled} type='primary' onClick={() => manageInputs(true, locationId)}>Select All</Button>
      <Button disabled={disabled} type='primary' onClick={() => manageInputs(false, locationId)}>Deselect All</Button>
    </>
  )
  if(!sentValues.find(game => game.gameId === value.gameId && `${game.locationId}` === `${locationId}`)) return (
    <input
      disabled={disabled}
      id={value.gameId}
      data-location={locationId}
      type="checkbox"
      onClick={() => checkbody()}
   />
  )
  if(sentValues.find(game => value.gameId && `${game.locationId}` === `${locationId}`)) return (
    <Tag 
      color="green"
    >
      Closed 
    </Tag>
  )
  return <></>
}

export default LocationTable